import React, { useEffect, useRef, useState } from "react";
import { renderAsync } from "docx-preview";
import Button from "@mui/material/Button";

export interface DocxViewerProps {
  /** Blob URL or http(s) URL to a .docx or .doc file */
  source: string;
  fileName?: string;
  /** Force the legacy-doc download fallback even when fileName lacks a .doc extension */
  legacyDoc?: boolean;
}

const DocxViewer: React.FC<DocxViewerProps> = ({
  source,
  fileName,
  legacyDoc = false,
}) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);

  const isHttpUrl = source.startsWith("http://") || source.startsWith("https://");

  useEffect(() => {
    if (legacyDoc) return;
    const el = bodyRef.current;
    if (!el || !source) return;

    let cancelled = false;
    setError(null);
    el.innerHTML = "";

    (async () => {
      try {
        const res = await fetch(source, {
          ...(isHttpUrl
            ? {
                credentials: "include" as RequestCredentials,
                mode: "cors" as RequestMode,
              }
            : {}),
        });
        if (!res.ok) {
          throw new Error(`Failed to load document (${res.status})`);
        }
        const blob = await res.blob();
        if (cancelled) return;

        await renderAsync(blob, el, undefined, {
          inWrapper: true,
          breakPages: true,
          ignoreWidth: true,
          ignoreHeight: false,
          ignoreLastRenderedPageBreak: false,
        });
      } catch (e) {
        if (!cancelled) {
          setError(
            e instanceof Error ? e.message : "Could not render Word preview.",
          );
        }
      }
    })();

    return () => {
      cancelled = true;
      el.innerHTML = "";
    };
  }, [source, legacyDoc, isHttpUrl]);

  if (!source) return null;

  // .doc files (always blob URLs in practice) cannot be rendered by docx-preview
  if (legacyDoc) {
    return (
      <div className="flex-1 min-h-0 overflow-auto p-6 flex flex-col items-center justify-center gap-3 text-center text-md text-hex-gray-600 border-t border-hex-gray-100">
        <p className="max-w-md">
          Legacy Word (.doc) files cannot be previewed directly in the browser.
        </p>
        <Button component="a" href={source} download={fileName || "document.doc"} variant="contained" color="primary">
          Download
        </Button>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col border-t border-hex-gray-100 text-hex-gray-800">
      {error ? (
        <div className="flex min-h-[120px] flex-1 flex-col items-center justify-center gap-3 overflow-auto p-6 text-center text-md text-hex-gray-600">
          <p className="max-w-md">{error}</p>
          {isHttpUrl ? (
            <Button component="a" href={source} target="_blank" rel="noopener noreferrer" variant="contained" color="primary">
              Open file
            </Button>
          ) : null}
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-auto overscroll-contain p-2">
          <div
            ref={bodyRef}
            className={[
              "docx-preview-root min-h-[200px] w-full min-w-0 max-w-full box-border",
              "[&_.docx-wrapper]:w-full! [&_.docx-wrapper]:max-w-full! [&_.docx-wrapper]:min-w-0!",
              "[&_.docx-wrapper]:items-stretch! [&_.docx-wrapper]:bg-transparent! [&_.docx-wrapper]:p-0! [&_.docx-wrapper]:pb-0!",
              "[&_section.docx]:w-full! [&_section.docx]:max-w-full! [&_section.docx]:min-w-0! [&_section.docx]:box-border!",
            ].join(" ")}
            aria-label={fileName ? `Word preview: ${fileName}` : "Word document preview"}
          />
        </div>
      )}
    </div>
  );
};

export default DocxViewer;
